import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError$,
  debounceTime$,
  distinctUntilChanged$,
  map$,
  switchMap$,
} from '@gh/rx/operators';
import { uniqueId } from 'lodash';
import { ENVIRONMENT, MAPBOX_ACCESS_TOKEN, REGION_CODE } from '@gh/config';
import {
  MapboxSuggestion,
  MapboxRetrieve,
  MapboxSuggestResponse,
  ReverseGeocodeResponse,
  ReverseGeocodeFeature,
  DetailedAddressContext,
  AddressComponents,
  ReverseGeocodeFeatureContext,
  SearchBoxReverseFeatureCollection,
} from './types';

@Injectable({
  providedIn: 'root'
})
export class MapboxService {
  private readonly SUGGEST_API = 'https://api.mapbox.com/search/searchbox/v1/suggest';
  private readonly RETRIEVE_API = 'https://api.mapbox.com/search/searchbox/v1/retrieve';
  private readonly SEARCH_BOX_REVERSE_API = 'https://api.mapbox.com/search/searchbox/v1/reverse';

  private sessionToken = this.generateSessionToken();

  constructor(private http: HttpClient) { }

  private generateSessionToken(): string {
    return uniqueId('session-');
  }

  resetSessionToken(): void {
    this.sessionToken = this.generateSessionToken();
  }

  private getMapboxCountryCode(): string {
    return ['prod'].includes(ENVIRONMENT) ? (REGION_CODE || '').trim() : '';
  }

  private createParams(
    params: Record<string, string | undefined>,
    options: { includeSessionToken?: boolean } = {},
  ): string {
    const { includeSessionToken = true } = options;
    const filteredParams: Record<string, string> = {};
  
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== undefined) {
        filteredParams[key] = params[key] as string;
      }
    }
  
    const searchParams = new URLSearchParams({
      access_token: MAPBOX_ACCESS_TOKEN || '',
      ...(includeSessionToken ? { session_token: this.sessionToken } : {}),
      ...filteredParams
    });
  
    return searchParams.toString();
  }  

  searchSuggestions(query: string): Observable<MapboxSuggestion[]> {
    if (!query.trim()) return of([]);

    const queryString = this.createParams({ q: query, country: this.getMapboxCountryCode() });
    const url = `${this.SUGGEST_API}?${queryString}`;

    return this.http.get<MapboxSuggestResponse>(url).pipe(
      map$((response) => response.suggestions || []),
      catchError$((error) => {
        console.error('Mapbox suggest error:', error);
        return of([]);
      })
    );
  }

  retrieveSuggestion(id: string): Observable<MapboxRetrieve | null> {
    const url = `${this.RETRIEVE_API}/${id}?${this.createParams({})}`;

    return this.http.get<MapboxRetrieve>(url).pipe(
      catchError$((error) => {
        console.error('Mapbox retrieve error:', error);
        return of(null);
      })
    );
  }

  createSearchStream(searchSubject: Subject<string>): Observable<MapboxSuggestion[]> {
    return searchSubject.pipe(
      debounceTime$(300),
      distinctUntilChanged$(),
      switchMap$((query) => this.searchSuggestions(query))
    );
  }

  getAddressComponents(suggestion: MapboxRetrieve): AddressComponents {
    const feature = suggestion?.features?.[0];
    if (!feature) return this.emptyAddress();

    const context = feature.properties.context as DetailedAddressContext;

    return {
      streetNo: context?.address?.address_number || '',
      street: context?.street?.name || '',
      postalCode: context?.postcode?.name || '',
      city: context?.locality?.name || context?.place?.name || '',
      state: context?.region?.region_code || '',
      stateName: context?.region?.name || '',
      country: context?.country?.name || '',
      coordinates: feature.geometry.coordinates || []
    };
  }

  getUpdatedComponents(response: ReverseGeocodeResponse): AddressComponents {
    const feature = response?.features?.[0];
    if (!feature) return this.emptyAddress();

    const context = feature.context || [];

    const find = (type: string) =>
      context.find((item: ReverseGeocodeFeatureContext) => item.id.startsWith(`${type}.`));

    const regionShort = find('region')?.short_code;
    let state = '';
    if (regionShort) {
      const dash = regionShort.indexOf('-');
      state = dash >= 0 ? regionShort.slice(dash + 1) : regionShort;
    }

    return {
      streetNo: feature.address || '',
      street: feature.text || '',
      postalCode: find('postcode')?.text || '',
      city: find('locality')?.text || find('place')?.text || '',
      state,
      country: find('country')?.text || '',
      coordinates: feature.center || []
    };
  }

  reverseGeocode([lng, lat]: [number, number]): Observable<ReverseGeocodeResponse> {
    const queryString = this.createParams(
      {
        longitude: String(lng),
        latitude: String(lat),
        limit: '1',
        country: this.getMapboxCountryCode(),
      },
      { includeSessionToken: false },
    );
    const url = `${this.SEARCH_BOX_REVERSE_API}?${queryString}`;
    return this.http.get<SearchBoxReverseFeatureCollection>(url).pipe(
      map$((fc) => this.searchBoxReverseToLegacyResponse(fc, [lng, lat])),
      catchError$((error) => {
        console.error('Mapbox reverse geocode error:', error);
        return of({
          type: 'FeatureCollection',
          query: [lng, lat],
          features: [],
        } as ReverseGeocodeResponse);
      }),
    );
  }

  private searchBoxReverseToLegacyResponse(
    fc: SearchBoxReverseFeatureCollection,
    query: [number, number],
  ): ReverseGeocodeResponse {
    const feature = fc.features?.[0];
    if (!feature) {
      return { type: 'FeatureCollection', query, features: [] };
    }

    const p = feature.properties;
    const ctx = p.context;
    const contextItems: ReverseGeocodeFeatureContext[] = [];

    if (ctx?.country) {
      const id = ctx.country.id || '_';
      contextItems.push({
        id: `country.${id}`,
        mapbox_id: id,
        text: ctx.country.name || '',
      });
    }
    if (ctx?.region) {
      const id = ctx.region.id || '_';
      const rc = ctx.region.region_code;
      const regionShortCode =
        ctx.region.region_code_full ||
        (rc && ctx.country?.country_code && !rc.includes('-')
          ? `${ctx.country.country_code}-${rc}`
          : rc);
      contextItems.push({
        id: `region.${id}`,
        mapbox_id: id,
        text: ctx.region.name || '',
        short_code: regionShortCode,
      });
    }
    if (ctx?.postcode) {
      const id = ctx.postcode.id || '_';
      contextItems.push({
        id: `postcode.${id}`,
        mapbox_id: id,
        text: ctx.postcode.name || '',
      });
    }
    if (ctx?.place) {
      const id = ctx.place.id || '_';
      contextItems.push({
        id: `place.${id}`,
        mapbox_id: id,
        text: ctx.place.name || '',
      });
    }
    if (ctx?.locality) {
      const id = ctx.locality.id || '_';
      contextItems.push({
        id: `locality.${id}`,
        mapbox_id: id,
        text: ctx.locality.name || '',
      });
    }

    const streetNo = ctx?.address?.address_number || '';
    const street = ctx?.street?.name || '';

    const legacyFeature: ReverseGeocodeFeature = {
      id: p.mapbox_id,
      type: 'Feature',
      place_type: [p.feature_type || 'address'],
      relevance: 1,
      properties: {
        mapbox_id: p.mapbox_id,
      },
      text: street || p.name || '',
      place_name: p.full_address || p.name || '',
      center: [...feature.geometry.coordinates] as [number, number],
      geometry: feature.geometry,
      address: streetNo,
      context: contextItems,
    };

    return { type: 'FeatureCollection', query, features: [legacyFeature] };
  }

  private emptyAddress(): AddressComponents {
    return {
      streetNo: '',
      street: '',
      postalCode: '',
      city: '',
      state: '',
      country: '',
      coordinates: []
    };
  }
}
